import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, MapPin, Wifi } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// Where a service can be booked: any mix of centers and Online, each with an optional own price and room.
// Saves only the locations — no other service setting, availability or booking is touched.
export default function ServiceLocationsEditor({ serviceId, testid = "svc-locations" }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/services/${serviceId}/locations-editor`).then((r) => { if (live) { setData(r.data); setRows(r.data.service_locations.map((x) => ({ ...x }))); } })
      .catch((e) => live && toast.error(formatApiError(e.response?.data?.detail)));
    return () => { live = false; };
  }, [serviceId]);
  if (!data) return <Loader2 size={16} className="animate-spin text-[#144F75]" />;
  const optName = (id) => (id === "virtual" ? "Online / Virtual" : data.options.find((o) => o.id === id)?.name);
  const editable = (r) => (r.location_id === "virtual" ? data.can_virtual !== false : data.allowed_all || data.options.some((o) => o.id === r.location_id));
  const used = new Set(rows.map((r) => r.location_id));
  const choices = [...data.options.filter((o) => !used.has(o.id)), ...(used.has("virtual") || data.can_virtual === false ? [] : [{ id: "virtual", name: "Online / Virtual" }])];
  const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.put(`/services/${serviceId}/locations`, {
        service_locations: rows.filter(editable).map((r) => ({ location_id: r.location_id, mode: r.mode, room: r.room || "", price: r.price === "" || r.price == null ? null : Number(r.price) })),
      });
      setRows(out.service_locations); setData((d) => ({ ...d, is_legacy: false }));
      toast.success("Locations saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-[#E5E0D8] bg-[#F9F7F3] p-4" data-testid={testid}>
      <div>
        <p className="text-sm font-semibold text-[#1A242B]">Where this can be booked</p>
        <p className="text-xs text-[#4A5D6B]">Add every center and/or Online. Leave a price blank to use the standard price ({`$${Number(data.base_price || 0).toFixed(2)}`}). Only locations change here.</p>
        {data.is_legacy && <p className="text-xs text-[#B5794E] mt-1" data-testid={`${testid}-legacy`}>Showing the current single location. Nothing changes until you save.</p>}
      </div>
      {rows.map((r, i) => {
        const opt = data.options.find((o) => o.id === r.location_id);
        const locked = !editable(r);
        return (
          <div key={r.location_id} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-[#E5E0D8] p-2.5" data-testid={`${testid}-row-${r.location_id}`}>
            {r.location_id === "virtual" ? <Wifi size={15} className="text-[#144F75]" /> : <MapPin size={15} className="text-[#144F75]" />}
            <span className="text-sm font-medium text-[#1A242B] min-w-[140px]">{optName(r.location_id) || r.name || "Center"}</span>
            {r.location_id !== "virtual" && !opt?.own && (
              <>
                <select value={r.mode} disabled={locked} onChange={(e) => set(i, { mode: e.target.value })} aria-label="Format" className="eyw-input !py-1.5 text-sm" data-testid={`${testid}-mode-${r.location_id}`}>
                  <option value="in_person">In person</option>
                  <option value="hybrid">In person or online</option>
                </select>
                <select value={r.room || ""} disabled={locked} onChange={(e) => set(i, { room: e.target.value })} aria-label="Room" className="eyw-input !py-1.5 text-sm" data-testid={`${testid}-room-${r.location_id}`}>
                  <option value="">Any room</option>
                  {r.room && !(opt?.rooms || []).some((rm) => rm.key === r.room) && <option value={r.room}>{r.room} (current)</option>}
                  {(opt?.rooms || []).map((rm) => <option key={rm.key} value={rm.key}>{rm.name}</option>)}
                </select>
              </>
            )}
            <input type="number" min="0" step="0.01" value={r.price ?? ""} disabled={locked} onChange={(e) => set(i, { price: e.target.value })} placeholder="Same price"
              aria-label="Price at this location" className="eyw-input !py-1.5 text-sm w-36" data-testid={`${testid}-price-${r.location_id}`} />
            {locked ? <span className="text-[11px] text-[#8FA89B]">Managed by the center or admin</span> : (
              <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label={`Remove ${optName(r.location_id)}`} data-testid={`${testid}-remove-${r.location_id}`} className="ml-auto text-[#8FA89B] hover:text-red-600"><Trash2 size={15} /></button>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-2">
        {choices.length > 0 && (
          <select value="" onChange={(e) => e.target.value && setRows((rs) => [...rs, { location_id: e.target.value, mode: e.target.value === "virtual" ? "virtual" : "in_person", room: "", price: null }])}
            className="eyw-input !py-1.5 text-sm" data-testid={`${testid}-add`} aria-label="Add a location">
            <option value="">+ Add a location…</option>
            {choices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <button type="button" onClick={save} disabled={busy || !rows.length} data-testid={`${testid}-save`} className="eyw-btn !py-2 text-sm disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} className="hidden" />} Save locations
        </button>
      </div>
      {!data.allowed_all && <p className="text-[11px] text-[#8FA89B]">You can add centers you're approved at, plus Online.</p>}
    </div>
  );
}
